/* eslint-disable no-param-reassign */
import {
  ref,
  watchEffect,
} from 'vue'
import type {
  ComponentPublicInstance,
  Ref,
} from 'vue'
import {
  createPopper,
} from '@popperjs/core'
import type {
  Options as PopperOptions,
} from '@popperjs/core'

type TemplateRef = HTMLElement | ComponentPublicInstance
type RefsTemplateRef = Ref<null|HTMLElement>
type PopoverState = {
  targetRef: RefsTemplateRef
  popperRef: RefsTemplateRef
  update: () => void
}

function getELement(value: TemplateRef) {
  if ((value as ComponentPublicInstance).$el) {
    return (value as ComponentPublicInstance).$el
  }
  return value
}

export function usePopperEffect(
  isShownState: Ref<boolean>,
  target: RefsTemplateRef,
  popper: RefsTemplateRef,
  options: PopperOptions,
): {
    update: () => void
  } {
  let popperInstance: ReturnType<typeof createPopper>|null
  watchEffect(onInvalidate => {
    if (!target.value || !popper.value) {
      return
    }
    if (isShownState.value) {
      if (popperInstance) {
        popperInstance?.destroy()
        popperInstance = null
      }
      popperInstance = createPopper(
        getELement(target.value),
        getELement(popper.value),
        options,
      )
    } else {
      popperInstance?.destroy()
      popperInstance = null
    }
    onInvalidate(() => {
      popperInstance?.destroy()
      popperInstance = null
    })
  }, {
    flush: 'post',
  })
  return {
    update: () => {
      popperInstance?.update()
    },
  }
}
type PopoverPartial = {
  [P in keyof PopperOptions]?: PopperOptions[P]
} & {
  offsetX?: number
  sameWidth?: boolean
  sameWidthPad?: number
}

export default function usePopper(
  isShownState: Ref<boolean>,
  props: PopoverPartial,
): PopoverState {
  const targetRef: RefsTemplateRef = ref(null)
  const popperRef: RefsTemplateRef = ref(null)
  const {
    offsetX = 0,
    sameWidth, sameWidthPad, ...restOptions
  } = props
  const options: PopperOptions = {
    placement: 'bottom-start',
    modifiers: [
      {
        name: 'myArrow',
        enabled: true,
        phase: 'afterMain',
        fn: ({ state }) => {
          if (['top-start', 'bottom-start'].includes(state.placement)) {
            state.modifiersData.arrow = {
              x: 8,
              centerOffset: 0,
            }
          }
          if (['top-end', 'bottom-end'].includes(state.placement)) {
            state.modifiersData.arrow = {
              x: state.rects.popper.width - 20,
              centerOffset: 0,
            }
          }
        },
      },
      {
        name: 'flip',
        enabled: true,
      },
      {
        name: 'offset',
        options: {
          offset: [offsetX, 8],
        },
      },
    ],
    strategy: 'absolute',
    ...(restOptions || {}),
  }
  if (sameWidth) {
    options.modifiers.push({
      name: 'sameWidth',
      enabled: true,
      phase: 'beforeWrite',
      requires: ['computeStyles'],
      fn: ({ state }) => {
        state.styles.popper.width = `${state.rects.reference.width + (sameWidthPad ?? 0)}px`
      },
      effect: ({ state }) => {
        state.elements.popper.style.width = `${
          (state.elements.reference as HTMLElement)?.offsetWidth + (sameWidthPad ?? 0)
        }px`
      },
    })
  }

  const { update } = usePopperEffect(isShownState, targetRef, popperRef, options)
  return {
    targetRef,
    popperRef,
    update,
  }
}
